'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import type { DockMarker } from '@/components/dashboard/DroneMap';
import { DOCK_ROSTER } from '@/lib/copy';
import {
  dockSymbolState,
  DOCK_STATE_COLORS,
  type DockSymbolState,
} from '@/components/dashboard/droneMapGeoJson';

/**
 * The word gets a theme-aware foreground token. The square keeps the marker hue.
 *
 * `DOCK_STATE_COLORS` are fill values for map symbols, and painted as TEXT they
 * are unreadable: on the light theme's chip ground, `busy` amber sits at about
 * 1.7:1 and `ready` green at about 1.8:1 — and in dark mode the grey that means
 * "this dock is not there" reaches only ~2.4:1. `--status-*-fg` exists for
 * exactly this case and darkens per theme, clearing 4.5:1 in both.
 *
 * Keeping the raw hue on the square is the point, not an oversight: it leaves
 * the colour as the redundant SECOND carrier behind the word, which is the rule
 * DroneSelectionChip already wrote down for flight state.
 */
const STATE_TEXT: Record<DockSymbolState, string> = {
  ready: 'text-status-green-fg',
  busy: 'text-status-amber-fg',
  offline: 'text-status-gray-fg',
  emergency: 'text-status-red-fg',
};

interface CopDockRosterProps {
  docks: DockMarker[];
  selectedDockId: string | null;
  /** Selecting a dock opens the SAME CopDockSheet the map-marker click opens. */
  onSelect: (id: string) => void;
  /**
   * Show every dock at once instead of paging through them.
   *
   * Lifted rather than local because the WIDTH is not this component's to set:
   * the row sizes to its content, and the element that carries that width is
   * the `pointer-events-auto` wrapper in `page.tsx`. Collapsed it must be
   * `w-fit` so the empty half of the band is map the operator can click;
   * expanded it must be full width or the chips have nothing to wrap inside.
   */
  expanded: boolean;
  /** Absent = the host does not offer expansion, and no control is drawn. */
  onToggleExpanded?: () => void;
}

/**
 * One row. Nothing more.
 *
 * The roster used to cost the map two stacked rows — a `DOCKS`/count header
 * strip, and below it a track of fixed-width two-line cards flanked by 36 px
 * chevrons. Three stacked text lines and four paddings deep, all of it above
 * the stage, most of it spent on five words that never change.
 *
 * So label, docks, paging and count all share ONE line, and every dock is a
 * single-line chip: dot, callsign, state square, state.
 *
 * THE EXPANDED FORM, and why this docblock used to forbid one. It said there was
 * "deliberately no expanded form and no control to grow it", because an
 * operations surface that can eat the airspace it reports on is the exact
 * failure #325 fixed for the selected-drone card. The founder asked for the
 * control anyway on 2026-09-08 - "it should be auto adjusted and in case there
 * are too many, we have a button to expand it or collapse it" - so the blanket
 * prohibition is gone and a BOUND takes its place. The #325 lesson is kept as
 * the constraint it always was, rather than as a ban:
 *
 *   - The control exists only at `lg` and up. Below that, `CopTopStack` is
 *     `contents`, so this bar sits in normal flow and anything it grows by comes
 *     straight out of the map's height. At `lg` it is `absolute inset-x-0 top-0`
 *     and floats OVER the stage instead of displacing it. The track therefore
 *     stays a single scrolling line below `lg` whatever `expanded` says, so a
 *     window narrowed while expanded cannot strand the operator in a tall bar.
 *   - Expanded, the wrap is capped at `20vh` and scrolls inside that. The panel
 *     can no longer grow without limit, which is the property #325 was really
 *     about. At any realistic roster the cap never engages: 20vh is about six
 *     rows, and a row holds a dozen chips.
 */
export function CopDockRoster({
  docks,
  selectedDockId,
  onSelect,
  expanded,
  onToggleExpanded,
}: CopDockRosterProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const maxLeft = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(maxLeft - el.scrollLeft > 4);
    };

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    // Feature-detected like the repo's other self-sizing surfaces
    // (useMeasuredSize, DroneMap): a runtime without RO must lose the chevrons,
    // not take the COP page down on mount.
    if (typeof ResizeObserver === 'undefined') {
      return () => el.removeEventListener('scroll', updateScrollState);
    }

    const resizeObserver = new ResizeObserver(updateScrollState);
    // BOTH boxes, and they answer different questions. The viewport is
    // `flex-1`, so its width is set by the row and never by what is inside it —
    // observing it alone catches the window resizing but NOT the chips growing.
    // The track's box IS the content width, so it fires when a dock joins the
    // roster and when a late font swap widens every callsign. Without it those
    // docks sit off-screen with no chevron to say they exist, which is the one
    // failure this bar is not allowed to have.
    resizeObserver.observe(el);
    if (trackRef.current) resizeObserver.observe(trackRef.current);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
    // Refs and the observers cover every input; re-subscribing per `docks`
    // identity would tear down and rebuild the listener on every dock_status
    // frame, forcing a synchronous reflow on the page that also hosts MapLibre.
  }, []);

  // Selecting a dock opens the sheet, which takes ~30% of the viewport out of
  // this row — several chips' worth. Without this, clicking the rightmost dock
  // scrolls the dock you are now commanding off behind the chevron, and a
  // selection made from a map marker never appears on the bar at all.
  useEffect(() => {
    if (!selectedDockId) return;
    const chip = viewportRef.current?.querySelector('[aria-pressed="true"]');
    // jsdom implements no layout and so no scrollIntoView.
    if (chip && typeof chip.scrollIntoView === 'function') {
      chip.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [selectedDockId]);

  /**
   * Whether the chips need more room than the row has. Drives the expand
   * control's EXISTENCE, not just its state: a button that does nothing on a
   * two-dock roster is a control the operator has to learn is pointless.
   */
  const overflowing = canScrollLeft || canScrollRight;

  const scrollByPage = (direction: -1 | 1) => {
    const el = viewportRef.current;
    if (!el) return;
    const amount = Math.max(220, Math.floor(el.clientWidth * 0.7));
    // globals.css's reduced-motion block only silences four named CSS
    // animations; a scripted smooth scroll runs straight through it.
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: direction * amount, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    // `surface-chrome` rather than `glass-card` since #505 slice 7 (D47).
    //
    // `--glass-bg` is `rgba(255,255,255,.05)`, and `globals.css` records that the
    // `--status-*-fg` tokens were measured over a `glass-card` composited over
    // `--background`, landing at 4.55 to 4.61:1 - a tenth of a point above the
    // 4.5 floor. That composite was accurate while this was a row ABOVE the map.
    // Floating it makes the backdrop the BASEMAP, which slice 6 gave the operator
    // a switcher for, so on satellite or a light basemap the state words, the
    // dock count and the selected chip sat on blurred imagery at no defined
    // ratio. `backdrop-blur` blurs; it does not raise contrast.
    //
    // `surface-chrome` is `--popover` at 92%, the ground the rest of the COP's
    // floating chrome already paints, so the measured tokens hold over it. The
    // other two children of the top and bottom stacks are opaque `bg-card`
    // already; this was the only translucent one.
    <div className="surface-chrome border border-subtle rounded-lg overflow-hidden flex items-center gap-2 px-2 py-1">
      <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
        Docks
      </span>

      {/* Paging stays VISIBLE rather than becoming a silent wheel-scroll: a dock
          that has scrolled out of view is a dock the operator does not know
          exists. The chevron is the disclosure. It reserves its space when
          there is nothing further that way, so arriving at an end never shifts
          the chips sideways under the cursor — but it is hidden from the
          accessibility tree while inert, so a roster that never scrolls does
          not announce two dead buttons. Safe next to `disabled`, which has
          already taken it out of the tab order. */}
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollLeft}
        aria-hidden={!canScrollLeft}
        aria-label="Scroll docks left"
        className={`shrink-0 inline-flex items-center justify-center size-6 rounded border border-subtle bg-popover/80 text-muted-foreground transition-colors ${
          canScrollLeft ? 'hover:bg-muted hover:text-foreground' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ChevronLeft className="size-3.5" />
      </button>

      <div
        ref={viewportRef}
        className={`flex-1 min-w-0 overflow-x-auto overflow-y-hidden scrollbar-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          expanded ? 'lg:overflow-x-hidden lg:overflow-y-auto lg:max-h-[20vh]' : ''
        }`}
      >
        {/* `min-w-max` is what makes the row one line and scrollable, and it stays
            unconditionally so the collapsed behaviour is what every viewport below
            `lg` gets. Only at `lg`, where the stack floats, does `expanded` release
            it and let the chips wrap. */}
        <div
          ref={trackRef}
          className={`flex gap-1.5 min-w-max ${expanded ? 'lg:flex-wrap lg:min-w-0' : ''}`}
        >
          {docks.map((d) => {
            const state = dockSymbolState(d.status, d.is_online);
            const color = DOCK_STATE_COLORS[state];
            const selected = d.id === selectedDockId;
            const callsign = d.callsign ?? d.id.slice(-6).toUpperCase();
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => onSelect(d.id)}
                aria-pressed={selected}
                // The state belongs in the accessible name: aria-label overrides
                // the chip's inner text, so without it the one word describing
                // the dock is the one thing a screen reader never hears.
                aria-label={`Operate ${callsign}, ${state}`}
                // `busy` collapses charging, swapping, tray_open, tray_moving
                // and awaiting_connection into one word. The bar is the right
                // place for that abstraction — "can I launch from this, yes or
                // no" — but the raw status costs nothing to carry here, and it
                // is what separates a dock that is 20 minutes from ready from
                // one whose handshake is broken and needs a human.
                title={`Operate ${callsign} — ${d.status.replace(/_/g, ' ')}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded border px-2 py-1 transition-colors ${
                  selected
                    // Matches StripBoard: a tint and a ring, never a full accent
                    // fill. The fill is a saturated blue in BOTH themes, and it
                    // does not restyle what sits on top — so the chip the
                    // operator is actively commanding became the one whose
                    // status word they could not read (red on blue, ~1:1).
                    ? 'border-hover bg-accent/15 ring-1 ring-inset ring-primary/50'
                    : 'border-subtle bg-popover/70 hover:bg-muted'
                }`}
              >
                <span
                  className={`size-2 rounded-full shrink-0 ${d.is_online ? 'bg-status-green-marker' : 'bg-status-gray-marker'}`}
                  aria-hidden
                />
                {/* Capped rather than fixed-width: five docks should not be
                    padded out to the width twenty would need. */}
                <span className="callsign text-[11px] text-foreground truncate max-w-[9rem]">
                  {callsign}
                </span>
                <span
                  className="size-2 rounded-[2px] border-[1.5px] shrink-0"
                  style={{ borderColor: color }}
                  aria-hidden
                />
                <span
                  className={`text-[9px] uppercase tracking-wide whitespace-nowrap ${STATE_TEXT[state]}`}
                >
                  {state}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollRight}
        aria-hidden={!canScrollRight}
        aria-label="Scroll docks right"
        className={`shrink-0 inline-flex items-center justify-center size-6 rounded border border-subtle bg-popover/80 text-muted-foreground transition-colors ${
          canScrollRight ? 'hover:bg-muted hover:text-foreground' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ChevronRight className="size-3.5" />
      </button>

      {/* Drawn ONLY when the chips do not fit, or while expanded so there is a
          way back. A permanent control on a two-dock roster is one more thing on
          the map that does nothing, and this bar's whole point is that it costs
          the stage as little as possible. */}
      {onToggleExpanded && (overflowing || expanded) && (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? DOCK_ROSTER.collapse : DOCK_ROSTER.expand}
          title={expanded ? DOCK_ROSTER.collapse : DOCK_ROSTER.expand}
          className="shrink-0 hidden lg:inline-flex items-center justify-center size-6 rounded border border-subtle bg-popover/80 text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
      )}

      <span className="shrink-0 text-[10px] font-mono text-muted-foreground tabular-nums">
        {docks.length}
      </span>
    </div>
  );
}
